import { api } from ".";
import {
  IChat,
  ISendMessageRequest,
  ISendMessageResponse,
  IOpenAiChatModel,
  ICreateModelRequest,
  IUpdateModelRequest,
  IListFilesResponse,
  IGetFileResponse,
  IUploadFileRequest,
  IUpdateFileRequest,
  IOpenAiFile,
  IPaginatedChatsResponse,
  IListChatsParams,
  IAssignedChatModel,
} from "../../types/Chat";

const chatQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    listChats: builder.query<
      IChat[],
      {
        accountId?: string;
        chatType?: "PatientChatBot" | "ContractManagementBot";
        orderExternalId?: string;
      }
    >({
      query: ({ accountId, chatType, orderExternalId }) => {
        const params = new URLSearchParams();
        if (accountId) params.append("accountId", accountId);
        if (chatType) params.append("chatType", chatType);
        if (orderExternalId) params.append("orderExternalId", orderExternalId);

        return {
          url: `chats?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: { data: IChat[] }) => response.data,
    }),
    /**
     * CLE-2811 — the model assigned to an account for a chat type, before any chat exists on it.
     * Non-sensitive fields only (no identity/guidelines/examples/files), so unlike the /models
     * management endpoints it is guarded by account access rather than SuperAdmin. 404 when the
     * account has no model assigned — callers treat that as "use the defaults".
     */
    getAssignedChatModel: builder.query<
      IAssignedChatModel,
      {
        accountId: string;
        chatType: "PatientChatBot" | "ContractManagementBot";
      }
    >({
      query: ({ accountId, chatType }) => ({
        url: `chats/assigned-model?accountId=${accountId}&chatType=${chatType}`,
        method: "GET",
      }),
      transformResponse: (response: { data: IAssignedChatModel }) =>
        response.data,
      providesTags: ["ChatModel"],
    }),
    listAllChats: builder.query<
      IPaginatedChatsResponse,
      IListChatsParams | void
    >({
      query: (params) => {
        const { page = 1, limit = 25, modelId, accountId } = params || {};
        const searchParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (modelId) {
          searchParams.append("modelId", modelId.toString());
        }

        if (accountId) {
          searchParams.append("accountId", accountId);
        }

        return {
          url: `chats/all?${searchParams.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: { data: IPaginatedChatsResponse }) =>
        response.data,
      providesTags: ["Chat"],
    }),
    getChat: builder.query<IChat, string>({
      query: (chatId) => ({
        url: `chats/${chatId}`,
        method: "GET",
      }),
      transformResponse: (response: { data: IChat }) => response.data,
      providesTags: (result, error, id) => [{ type: "Chat", id }],
    }),
    sendMessage: builder.mutation<ISendMessageResponse, ISendMessageRequest>({
      query: (messageData) => ({
        url: "chats/send_message",
        method: "POST",
        body: messageData,
      }),
      transformResponse: (response: { data: ISendMessageResponse }) =>
        response.data,
    }),
    listModels: builder.query<IOpenAiChatModel[], void>({
      query: () => ({
        url: "chats/models",
        method: "GET",
      }),
      transformResponse: (response: { data: IOpenAiChatModel[] }) =>
        response.data,
      providesTags: ["ChatModel"],
    }),
    getModel: builder.query<IOpenAiChatModel, number>({
      query: (modelId) => ({
        url: `chats/models/${modelId}`,
        method: "GET",
      }),
      transformResponse: (response: { data: IOpenAiChatModel }) =>
        response.data,
      providesTags: (result, error, id) => [{ type: "ChatModel", id }],
    }),
    createModel: builder.mutation<IOpenAiChatModel, ICreateModelRequest>({
      query: (modelData) => ({
        url: "chats/models",
        method: "POST",
        body: modelData,
      }),
      transformResponse: (response: { data: IOpenAiChatModel }) =>
        response.data,
      invalidatesTags: ["ChatModel"],
    }),
    updateModel: builder.mutation<
      IOpenAiChatModel,
      { id: number; data: IUpdateModelRequest }
    >({
      query: ({ id, data }) => ({
        url: `chats/models/${id}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response: { data: IOpenAiChatModel }) =>
        response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: "ChatModel", id },
        "ChatModel",
      ],
    }),
    listFiles: builder.query<IListFilesResponse, void>({
      query: () => ({
        url: "chats/files",
        method: "GET",
      }),
      transformResponse: (response: { data: IListFilesResponse }) =>
        response.data,
      providesTags: ["ChatFile"],
    }),
    getFile: builder.query<IGetFileResponse, string>({
      query: (fileId) => ({
        url: `chats/files/${fileId}`,
        method: "GET",
      }),
      transformResponse: (response: { data: IGetFileResponse }) =>
        response.data,
      providesTags: (result, error, id) => [{ type: "ChatFile", id }],
    }),
    uploadFile: builder.mutation<IOpenAiFile, IUploadFileRequest>({
      query: (fileData) => ({
        url: "chats/files/upload",
        method: "POST",
        body: fileData,
      }),
      transformResponse: (response: { data: IOpenAiFile }) => response.data,
      invalidatesTags: ["ChatFile"],
    }),
    deleteFile: builder.mutation<void, string>({
      query: (fileId) => ({
        url: `chats/files/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ChatFile"],
    }),
    updateFile: builder.mutation<
      IOpenAiFile,
      { fileId: string; data: IUpdateFileRequest }
    >({
      query: ({ fileId, data }) => ({
        url: `chats/files/${fileId}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response: { data: IOpenAiFile }) => response.data,
      invalidatesTags: (result, error, { fileId }) => [
        { type: "ChatFile", id: fileId },
        "ChatFile",
        "ChatModel",
      ],
    }),
  }),
});

export const {
  useListChatsQuery,
  useGetAssignedChatModelQuery,
  useListAllChatsQuery,
  useSendMessageMutation,
  useListModelsQuery,
  useGetModelQuery,
  useCreateModelMutation,
  useUpdateModelMutation,
  useListFilesQuery,
  useGetFileQuery,
  useUploadFileMutation,
  useUpdateFileMutation,
  useDeleteFileMutation,
  useGetChatQuery,
} = chatQueries;
